import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL!,
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY!
);

// Inloggningen hålls i minnet och uppdateras när Supabase meddelar en
// ändring. Förut hämtades den med getSession() före varje anrop till
// backend, och de anropen kunde fastna bakom Supabase lås (se nedan).
let aktuellToken: string | null = null;

function medTidsgräns<T>(löfte: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    löfte,
    new Promise<T>((_, avvisa) => setTimeout(() => avvisa(new Error('tidsgräns')), ms)),
  ]);
}

/** Inloggningens token för anrop till backend. Hämtas bara från Supabase när
 *  den saknas eller har förkastats, och aldrig längre än fem sekunder. */
export async function hämtaToken(): Promise<string | null> {
  if (aktuellToken) return aktuellToken;
  try {
    const svar: any = await medTidsgräns<any>(supabase.auth.getSession(), 5000);
    aktuellToken = svar?.data?.session?.access_token ?? null;
  } catch {
    // Fastnar Supabase går anropet iväg utan token; backend svarar 401.
  }
  return aktuellToken;
}

/** Glöm token, till exempel efter ett 401 från backend. */
export function glömToken() {
  aktuellToken = null;
}

const INAKTIVITETSGRÄNS_MS = 2 * 60 * 60 * 1000; // 2 timmar
const KONTROLLINTERVALL_MS = 60 * 1000; // kolla varje minut
const AKTIVITETSHÄNDELSER: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

interface Profil {
  id: string;
  namn: string;
  roll: 'admin' | 'säljare';
  aktiv: boolean;
  // Moduler användaren har tillgång till: restaurang, anbud, inkop.
  moduler?: string[];
}

interface AuthContextType {
  användare: any;
  profil: Profil | null;
  laddar: boolean;
  loggaUt: () => void;
  supabase: any;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [användare, setAnvändare] = useState<any>(null);
  const [profil, setProfil] = useState<Profil | null>(null);
  const [laddar, setLaddar] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }: any) => {
      aktuellToken = session?.access_token ?? null;
      setAnvändare(session?.user ?? null);
      if (session?.user) hämtaProfil(session.user.id);
      else setLaddar(false);
    });

    // Supabase håller ett lås medan den här funktionen körs. En databasfråga
    // härifrån väntar på samma lås och fastnar för gott, och då fastnar även
    // alla andra anrop. Därför görs ingen fråga här direkt: profilen hämtas
    // efteråt (setTimeout), och bara vid inloggning, inte när token förnyas.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((händelse: string, session: any) => {
      aktuellToken = session?.access_token ?? null;
      setAnvändare(session?.user ?? null);
      if (!session?.user) { setProfil(null); setLaddar(false); return; }
      if (händelse === 'SIGNED_IN' || händelse === 'USER_UPDATED' || händelse === 'INITIAL_SESSION') {
        const id = session.user.id;
        setTimeout(() => hämtaProfil(id), 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Loggar ut användaren automatiskt efter en längre period utan
  // aktivitet (mus, tangentbord, scroll, touch). Kollar en gång i
  // minuten mot en tidsstämpel istället för att förlita sig på en
  // enda lång setTimeout, som annars kan bli opålitlig i bakgrundsflikar.
  useEffect(() => {
    if (!användare) return;

    let senasteAktivitet = Date.now();
    const registreraAktivitet = () => { senasteAktivitet = Date.now(); };

    AKTIVITETSHÄNDELSER.forEach(händelse =>
      window.addEventListener(händelse, registreraAktivitet, { passive: true })
    );

    const intervall = setInterval(() => {
      if (Date.now() - senasteAktivitet >= INAKTIVITETSGRÄNS_MS) {
        loggaUt();
      }
    }, KONTROLLINTERVALL_MS);

    return () => {
      AKTIVITETSHÄNDELSER.forEach(händelse =>
        window.removeEventListener(händelse, registreraAktivitet)
      );
      clearInterval(intervall);
    };
  }, [användare]);

  async function hämtaProfil(userId: string) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (!data || data.aktiv === false) {
      await supabase.auth.signOut();
      setProfil(null);
      setLaddar(false);
      return;
    }
    setProfil(data);
    setLaddar(false);
  }

  async function loggaUt() {
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ användare, profil, laddar, loggaUt, supabase }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth måste användas inom AuthProvider');
  return context;
}

export { supabase };